import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * INTEGRATION PARTNERS
 *
 * The marks the integrations screens draw for a third party — Square,
 * RoadSync, QuickBooks, Urgently. A partner's logo is not an Avatar: it is
 * contained rather than cropped, sits on the partner's own brand colour, and
 * in the connection dialog stands inside a primary ring between the two
 * things being joined.
 */

/** The box a partner's logo sits in. */
export const partnerLogoRecipe = cva(
  [
    uiMarker,
    'inline-flex shrink-0 items-center justify-center overflow-hidden',
  ],
  {
    variants: {
      variant: {
        /** The card's square: brand colour behind it, from the partner data. */
        tile: 'size-12 rounded-lg',
        /** The dialog's circle: a primary ring on the app's own background. */
        ring: 'size-14 rounded-full border-2 border-primary bg-background shadow-e2',
      },
    },
    defaultVariants: { variant: 'tile' },
  }
);

/** Contained, never cropped — a wordmark has to stay whole. */
export const partnerLogoImageRecipe = cva([
  uiMarker,
  'max-h-8 max-w-8 object-contain',
]);

/**
 * The connection dialog's handshake: this device, a run of dots, the partner,
 * another run of dots, the company. The dots are a primary gradient rather
 * than a solid so the row reads as a link being made rather than a divider.
 */
export const handshakeRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex w-full items-center justify-center gap-3',
]);

export const handshakeDotsRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex gap-1',
]);

export const handshakeDotRecipe = cva([
  uiMarker,
  'size-1 rounded-full bg-gradient-to-r from-primary/20 via-primary/60 to-primary/20',
]);

export type PartnerLogoRecipeProps = VariantProps<typeof partnerLogoRecipe>;
